import type { StytchError } from '@stytch/vanilla-js';
import { router } from 'expo-router';
import * as React from 'react';
import {
    type LegacyRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import type { TextInput } from 'react-native';
import { Keyboard, KeyboardAvoidingView } from 'react-native';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    isWeb,
    OpenDevDashboardButton,
    PillButton,
    PrivateInput,
    SafeAreaView,
    useThemeColors,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { useDebouncedCallback } from 'use-debounce';

import Logo from '../../../../assets/images/logos/logo-new.svg';
import BaseEmailPasswordPage from '../../../../test/pages/auth/EmailPasswordPage';
import { isEmail } from '../../../common/misc';
import {
    IS_DEV,
    IS_PRODUCTION,
    IS_STAGING,
} from '../../../config/FruitfulEnvironment';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import { useUser } from '../../../user/providers/UserProvider';
import { SESSION_DURATION_MINUTES } from '../../constants';
import { useStytch } from '../../hooks/useAuth';
import { useRedirectToFinishSignup } from '../../hooks/useRedirectToFinishSignup';
import { useRefreshStalePage } from '../../hooks/useRefreshStalePage';
import ShowPasswordToggle from '../ShowPasswordToggle';
import StytchErrorMessage from '../StytchErrorMessage';
import CreateNewMembershipSheet from './CreateNewMembershipSheet';
import Greeting from './Greeting';
import LoginHelpSheet from './LoginHelpSheet';

const LoginEmail = ({
    headerMessage,
    includeSignupLink,
    onSuccess,
}: {
    headerMessage?: string;
    includeSignupLink?: boolean;
    onSuccess?: () => void;
}) => {
    useRefreshStalePage();
    const { getColor } = useThemeColors();
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('LoginEmail');

    const emailValue = useRef('');
    const passwordValue = useRef('');
    const [showPassword, setShowPassword] = useState(false);
    const [authError, setAuthError] = useState<StytchError | null>(null);
    const [helpSheetOpen, setHelpSheetOpen] = useState(false);
    const [createMembershipSheetOpen, setCreateMembershipSheetOpen] =
        useState(false);
    const [submitEnabled, setSubmitEnabled] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const { user } = useUser();
    const toast = useToastController();
    const stytch = useStytch();
    const { needsToFinishSignup, redirectToFinishSignup } =
        useRedirectToFinishSignup();

    const emailInput: LegacyRef<TextInput> | null = useRef(null);
    const passwordInput: LegacyRef<TextInput> | null = useRef(null);

    const setEmail = useCallback((value: string) => {
        emailValue.current = value.trim();
        validateForm();
    }, []);

    const setPassword = useCallback((value: string) => {
        LogRocketFacade.log('User typing in password field');
        passwordValue.current = value.trim();
        validateForm();
    }, []);

    const validateForm = () => {
        setAuthError(null);

        setSubmitEnabled(
            isEmail(emailValue.current) && passwordValue.current.length > 0,
        );
    };

    const submitEmail = useDebouncedCallback(
        useCallback(() => {
            passwordInput.current?.focus();
        }, []),
        500,
        { leading: true },
    );

    const submitForm = useDebouncedCallback(
        useCallback(async () => {
            if (submitEnabled) {
                setAuthError(null);
                await logIn();
            }
        }, [submitEnabled]),
        500,
        { leading: true },
    );

    const logIn = async () => {
        setIsSubmitting(true);
        setIsAuthenticated(false);

        try {
            const response = await stytch.passwords.authenticate({
                email: emailValue.current,
                password: passwordValue.current,
                session_duration_minutes: SESSION_DURATION_MINUTES,
            });

            if (response.status_code !== 200) {
                captureException(new Error('Login failed'), {
                    statusCode: response.status_code,
                });
                setIsSubmitting(false);
                emailInput.current?.focus();
            } else {
                setIsAuthenticated(true);
                Keyboard.dismiss();
                onSuccess?.();
            }
        } catch (error) {
            setAuthError(error as StytchError);
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (!isSubmitting || !isAuthenticated || onSuccess !== undefined) {
            // assumes custom onSuccess function will trigger a redirect
            return;
        }

        if (user?.isPartial) {
            router.navigate('/sign-in/otp');
        } else if (needsToFinishSignup) {
            setIsSubmitting(false);
            redirectToFinishSignup();
        }
    }, [
        isSubmitting,
        isAuthenticated,
        user?.isPartial,
        needsToFinishSignup,
        redirectToFinishSignup,
    ]);

    if (isSubmitting) {
        return <FullScreenSpinner />;
    }

    // If the signup link should never be present, we expect includeSignupLink to be false.
    // Otherwise, we default to including it on web and excluding it on native (to explicitly avoid connecting our signup flow to our native app).
    // However, in non-prod envs, we will show a different link for more prominent access to create new test users.
    if (includeSignupLink == null) {
        includeSignupLink = IS_PRODUCTION ? isWeb : true;
    }

    return (
        <SafeAreaView style={{ flex: 1, width: '100%' }}>
            <KeyboardAvoidingView
                style={{
                    flex: 1,
                    width: '100%',
                }}
                behavior={isWeb ? 'height' : 'padding'}
                keyboardVerticalOffset={16}
                ignoreIOSKeyboardWillChangeEvents={true}
                onTouchStart={() => {
                    toast.hide();
                }}
                onPointerDown={() => {
                    toast.hide();
                }}
            >
                <YStack
                    alignItems="center"
                    alignSelf="center"
                    width="100%"
                    flex={1}
                    paddingHorizontal={16}
                    marginTop={48}
                    gap={36}
                    maxWidth={450}
                    $platform-web={{
                        justifyContent: 'center',
                        marginTop: 0,
                    }}
                    $sm={{
                        gap: 64,
                    }}
                >
                    {headerMessage != null ? (
                        <YStack gap={12} alignItems="center">
                            <Logo height={24} color={getColor('$black')} />
                            <BodyMediumRegular textAlign="center">
                                {headerMessage}
                            </BodyMediumRegular>
                        </YStack>
                    ) : (
                        <Greeting />
                    )}

                    <YStack gap={12} width="100%">
                        <PrivateInput
                            ref={emailInput}
                            key="email"
                            placeholder="Email"
                            autoComplete="email"
                            inputMode="email"
                            keyboardType="email-address"
                            textContentType="username"
                            defaultValue={emailValue.current}
                            onChangeText={setEmail}
                            onSubmitEditing={submitEmail}
                            blurOnSubmit={false}
                            invalid={authError !== null}
                            testID={BaseEmailPasswordPage.emailInputId}
                        />

                        <View>
                            <PrivateInput
                                ref={passwordInput}
                                key="password"
                                placeholder="Password"
                                inputMode="text"
                                secureTextEntry={!showPassword}
                                keyboardType="default"
                                textContentType="password"
                                defaultValue={passwordValue.current}
                                onChangeText={setPassword}
                                onSubmitEditing={submitForm}
                                blurOnSubmit={false}
                                paddingRight={48}
                                invalid={authError !== null}
                                testID={BaseEmailPasswordPage.passwordInputId}
                            />
                            <ShowPasswordToggle
                                showPassword={showPassword}
                                setShowPassword={setShowPassword}
                            />
                        </View>

                        <YStack
                            gap={4}
                            alignItems={isWeb ? 'flex-start' : 'center'}
                        >
                            {authError && (
                                <StytchErrorMessage error={authError} />
                            )}

                            <XStack
                                justifyContent={
                                    isWeb ? 'space-between' : 'center'
                                }
                                width="100%"
                            >
                                <PillButton
                                    variant="tertiary"
                                    text="Trouble signing in?"
                                    onPress={() => {
                                        Keyboard.dismiss();
                                        setHelpSheetOpen(true);
                                    }}
                                />

                                {includeSignupLink && IS_PRODUCTION && (
                                    <PillButton
                                        variant="tertiary"
                                        text="Not a member yet?"
                                        onPress={() => {
                                            window.location.href =
                                                '/sign-up/get-started';
                                        }}
                                    />
                                )}
                                {includeSignupLink && !IS_PRODUCTION && (
                                    <PillButton
                                        variant="tertiary"
                                        text="Create new membership?"
                                        onPress={() => {
                                            setCreateMembershipSheetOpen(true);
                                        }}
                                    />
                                )}
                            </XStack>
                        </YStack>
                    </YStack>
                    <Button
                        size={ButtonSize.LARGE}
                        variant={ButtonVariant.PRIMARY}
                        text="Sign in"
                        onPress={submitForm}
                        disabled={!submitEnabled}
                        buttonProps={{
                            testID: BaseEmailPasswordPage.signInButtonId,
                            ...(!isWeb && { marginTop: 'auto' }),
                        }}
                    />
                </YStack>
            </KeyboardAvoidingView>

            <LoginHelpSheet
                isOpen={helpSheetOpen}
                onClose={() => {
                    setHelpSheetOpen(false);
                }}
            />

            <CreateNewMembershipSheet
                isOpen={createMembershipSheetOpen}
                onClose={() => {
                    setCreateMembershipSheetOpen(false);
                }}
            />

            <View
                style={{ position: 'absolute', top: isWeb ? 16 : 50, right: 8 }}
            >
                <OpenDevDashboardButton hidden={!(IS_DEV || IS_STAGING)} />
            </View>
        </SafeAreaView>
    );
};

export default LoginEmail;
